import './SinglePlayer.css';
import { useCallback, useEffect, useMemo, useReducer } from 'react';
import { getLegalMoves } from '../../service/LegalMovesService';
import { gameReducer, ACTIONS, initialState } from '../../reducers/GameReducer';
import { getBestMove } from '../../ai/GetBestMoveService';
import { initialState as initializeBoard } from '../../engine/InitializeGame';
import GameBoard from '../../engine/GameBoard';

function SinglePlayer() {
  const DEPTH = 4;

  const [state, dispatch] = useReducer(gameReducer, initialState);

  useEffect(() => {
    const init = initializeBoard;

    dispatch({
      type: ACTIONS.INITIALIZE_GAME,
      payload: { blocks: init.blocks, pieces: init.pieces }
    })
  }, [])

  // Dumb AI
  const makeAIMove = useCallback(() => {
    // Early return if not black's turn
    if (state.turn !== "black") return;

    if (state.result) return;

    if (state.ongoingChainCapture) {
      setTimeout(() => {

        const newPieces = state.pieces.map(p => ({ ...p }))
        const newBlocks = state.blocks.map(b => ({ ...b }))
        const bestMove = getBestMove(newPieces, newBlocks, "black", DEPTH, true)

        if (!bestMove) {
          console.warn('empty best move');

          return
        }

        dispatch({
          type: ACTIONS.MOVE_PIECE,
          payload: {
            x: bestMove.move.x,
            y: bestMove.move.y,
            selectedPiece: bestMove.piece
          }
        })
      }, 300);
      return;
    } else {

      const blackPieces = state.pieces.filter(p => p.playerType === "black");

      if (blackPieces.length === 0) {
        console.warn("No more black pieces to move");
        return;
      }
      const blackPiecesWithLegalMoves = [];

      blackPieces.forEach(bp => {
        const legalMoves = getLegalMoves(bp.x, bp.y, state.pieces, state.blocks);
        if (legalMoves?.length > 0) blackPiecesWithLegalMoves.push(bp);
      });

      // Guard: if no pieces can move, exit early
      if (blackPiecesWithLegalMoves.length === 0) {
        console.warn("No legal moves available for black");
        return;
      }

      // Adding delay here to make it more natural and might prevent bugs cause by firing moves too quickly
      setTimeout(() => {

        const newPieces = state.pieces.map(p => ({ ...p }))
        const newBlocks = state.blocks.map(b => ({ ...b }))
        const bestMove = getBestMove(newPieces, newBlocks, "black", DEPTH)

        if (!bestMove) {
          console.warn('empty best move');
          return
        }

        dispatch({
          type: ACTIONS.MOVE_PIECE,
          payload: {
            x: bestMove.move.x,
            y: bestMove.move.y,
            selectedPiece: bestMove.piece
          }
        })
      }, 300);
      return;
    }

  }, [state.pieces, state.blocks, state.ongoingChainCapture, state.result, state.turn])

  useEffect(() => {
    makeAIMove();
  }, [state.turn, makeAIMove]);

  const onPieceClick = useCallback((x, y) => {
    const piece = state.pieces.find(p => p.x === x && p.y === y)
    const block = state.blocks.find(b => b.x === x && b.y === y)

    if (state.result) {
      console.log('has a result');

      return;
    }

    if (piece) {
      dispatch({ type: ACTIONS.SELECT_PIECE, payload: { x, y } })
      return;
    }

    if (block?.highlighted) {

      if (!state.selectedPiece) return;

      dispatch({
        type: ACTIONS.MOVE_PIECE,
        payload: {
          x: x,
          y: y,
          selectedPiece: state.selectedPiece
        }
      })

      return;
    }
  }, [state])

  const undoMove = useCallback(() => {
    dispatch({
      type: ACTIONS.UNDO_MOVE
    });
  }, [])

  const restartGame = useCallback(() => {
    const init = initializeBoard;

    dispatch({
      type: ACTIONS.INITIALIZE_GAME,
      payload: { blocks: init.blocks, pieces: init.pieces }
    })

    dispatch({
      type: ACTIONS.RESTART_GAME,
      payload: { blocks: init.blocks, pieces: init.pieces }
    })
  }, [])

  const events = useMemo(() => ({
    onPieceClick,
    restartGame,
    undoMove,
  }), [onPieceClick, restartGame, undoMove]);


  return (
    <GameBoard gameState={state} events={events} />
  );
}

export default SinglePlayer;
